import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";

export default function AdminResetPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters.");
    if (pw !== confirmPw) return toast.error("Passwords do not match.");
    setBusy(true);
    try {
      await api.post("/admin/auth/reset", { token, new_password: pw });
      toast.success("Password updated. Please sign in.");
      navigate("/control-panel-x9/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err, "Reset failed"));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="mb-page flex min-h-screen items-center justify-center px-6 py-16" data-testid="admin-reset">
        <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-10 text-center shadow-mb-card">
          <p className="text-sm text-gray-600">Reset token missing or invalid.</p>
          <div className="mt-4">
            <Link to="/control-panel-x9/login" className="text-brand-navy underline">
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-page flex min-h-screen items-center justify-center px-6 py-16" data-testid="admin-reset">
      <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-10 shadow-mb-card">
        <div className="mb-6 flex items-center gap-2">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-navy/8 text-brand-navy">
            <ShieldCheck className="h-4 w-4" />
          </span>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">Restricted</div>
            <h1 className="text-lg font-bold tracking-tight text-gray-900">Set new password</h1>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-reset-form">
          <div>
            <Label htmlFor="ar-new" className="text-xs">New password</Label>
            <Input id="ar-new" type="password" required minLength={8} value={pw}
              onChange={(e) => setPw(e.target.value)} data-testid="admin-reset-pw" />
          </div>
          <div>
            <Label htmlFor="ar-confirm" className="text-xs">Confirm new password</Label>
            <Input id="ar-confirm" type="password" required minLength={8} value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)} data-testid="admin-reset-confirm" />
          </div>
          <Button type="submit" disabled={busy}
            className="w-full bg-brand-navy text-white hover:bg-brand-navy-hover"
            data-testid="admin-reset-submit">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Update password
          </Button>
        </form>
      </div>
    </div>
  );
}
